import { isAllowedInNesting, isAllowedInQuestionDeck, isDoubleElement } from './elementService';
import { type Entity } from '@editor2/types/entity';
import {
	type DoubleElements,
	type PageElement,
	type PollResults,
	ElementConfigName,
	ElementType
} from '@editor2/types/pageElements';
import { MAX_MCQ_IN_QUESTION_DECK } from '@editor2/constants/allowedInQuestionDeck';
import { intersection } from 'lodash-es';

export interface DoubleElementValidationPayload {
	draggableElement: DoubleElements;
	pageElements: string[];
	sourceIndex: number;
	destinationIndex: number;
	elements: Entity<PageElement>;
}

export interface InsideDoubleValidationPayload {
	pageElements: string[];
	sourceIndex: number;
	destinationIndex: number;
	elements: Entity<PageElement>;
}

export interface FindDirectionIndexPayload {
	elements: Entity<PageElement>;
	elementsSlice: string[];
	elementType: ElementType.NestingInstruction | ElementType.QuestionDeck;
	direction: string;
}

export const findDirectionIndex = (payload: FindDirectionIndexPayload) => {
	const { elements, elementsSlice, elementType, direction } = payload;

	const nestingIndex = elementsSlice.findIndex((id) => {
		const element = elements.entities[id];
		return element.type === elementType && element.direction === direction;
	});

	return nestingIndex;
};

export const validateDoubleNesting = (payload: DoubleElementValidationPayload) => {
	const { draggableElement, pageElements, sourceIndex, destinationIndex, elements } = payload;

	const errorMessage = 'Rejected: Double Nesting is not allowed';

	switch (draggableElement.direction) {
		case 'in': {
			const elementsSlice = pageElements.slice(0, sourceIndex).reverse(); // elements before
			const payload = {
				elements,
				elementsSlice,
				elementType: draggableElement.type,
				direction: 'out'
			};

			const firstDirectionOutIndex = findDirectionIndex(payload);
			if (
				firstDirectionOutIndex >= 0 &&
				destinationIndex <= sourceIndex - firstDirectionOutIndex - 1
			) {
				throw new Error(errorMessage);
			}
			break;
		}

		case 'out': {
			const elementsSlice = pageElements.slice(sourceIndex); // elements after
			const payload = {
				elements,
				elementsSlice,
				elementType: draggableElement.type,
				direction: 'in'
			};

			const firstDirectionInIndex = findDirectionIndex(payload);
			if (firstDirectionInIndex >= 0 && destinationIndex >= firstDirectionInIndex + sourceIndex) {
				throw new Error(errorMessage);
			}
			break;
		}

		default:
			break;
	}
};

export const validateDirectionOrder = (payload: DoubleElementValidationPayload) => {
	const { draggableElement, pageElements, sourceIndex, destinationIndex, elements } = payload;

	const errorMessage = 'Rejected: Direction order should be IN - OUT.';

	switch (draggableElement.direction) {
		case 'in': {
			const elementsSlice = pageElements.slice(sourceIndex); // elements after
			const payload = {
				elements,
				elementsSlice,
				elementType: draggableElement.type,
				direction: 'out'
			};

			const firstDirectionOutIndex = findDirectionIndex(payload);
			if (firstDirectionOutIndex >= 0 && destinationIndex >= firstDirectionOutIndex + sourceIndex) {
				throw new Error(errorMessage);
			}
			break;
		}

		case 'out': {
			const elementsSlice = pageElements.slice(0, sourceIndex).reverse(); // elements before
			const payload = {
				elements,
				elementsSlice,
				elementType: draggableElement.type,
				direction: 'in'
			};

			const firstDirectionInIndex = findDirectionIndex(payload);
			if (
				firstDirectionInIndex >= 0 &&
				destinationIndex <= sourceIndex - firstDirectionInIndex - 1
			) {
				throw new Error(errorMessage);
			}
			break;
		}

		default:
			break;
	}
};

const validateAllowedInQuestionDeck = (elementId: string, elements: Entity<PageElement>) => {
	const item = elements.entities[elementId];

	if (!isAllowedInQuestionDeck(item.type)) {
		const itemName = ElementConfigName[item.type];
		throw new Error(`${itemName} is not allowed inside Question Deck`);
	}
};

const validateMutuallyExclusiveTypesInQuestionDeck = (
	newElementsInside: string[],
	questionDeckTypes: ElementType[],
	elements: Entity<PageElement>
) => {
	const assertNewElementsNotMutuallyExclusive = (...currentTypes: ElementType[]): void => {
		if (intersection(questionDeckTypes, currentTypes).length > 0) {
			newElementsInside.forEach((elementId) => {
				const newElementType = elements.entities[elementId].type;
				const newElementName = ElementConfigName[newElementType];
				const currentTypesNames = currentTypes.map((type) => ElementConfigName[type]).join(' or ');

				if (!currentTypes.includes(newElementType))
					throw new Error(
						`"${newElementName}" and "${currentTypesNames}" cannot exist in the same Question Deck.`
					);
			});
		}
	};

	assertNewElementsNotMutuallyExclusive(ElementType.MCQuestion);
	assertNewElementsNotMutuallyExclusive(ElementType.MCQuestionPool);
	assertNewElementsNotMutuallyExclusive(ElementType.Poll, ElementType.PollResults);
};

const validatePollAliasesInQuestionDeck = (
	elementsInside: string[],
	elements: Entity<PageElement>
) => {
	const pollAliasIds = elementsInside
		.map((elementId) => elements.entities[elementId])
		.filter((element): element is PollResults => element.type === ElementType.PollResults)
		.map((element) => element.poll_question_family_id)
		.filter(Boolean);

	const isUnique = new Set(pollAliasIds).size === pollAliasIds.length;
	if (!isUnique) throw new Error('Question Deck cannot contain multiple aliases of the same poll.');
};

const validateAllowedInNestingInstruction = (elementId: string, elements: Entity<PageElement>) => {
	const item = elements.entities[elementId];

	if (!isAllowedInNesting(item.type)) {
		const itemName = ElementConfigName[item.type];
		throw new Error(`${itemName} is not allowed inside Nesting Instruction`);
	}
};

const getNewElementsInside = (payload: DoubleElementValidationPayload) => {
	const { draggableElement, pageElements, sourceIndex, destinationIndex } = payload;

	return draggableElement.direction === 'in'
		? pageElements.slice(destinationIndex, sourceIndex)
		: pageElements.slice(sourceIndex + 1, destinationIndex + 1);
};

const getCurrentElementsInside = (payload: DoubleElementValidationPayload) => {
	const { draggableElement, pageElements, sourceIndex, destinationIndex, elements } = payload;

	let currentElementsInside = [];
	const commonPayload = { elements, elementType: draggableElement.type };

	switch (draggableElement.direction) {
		case 'in': {
			const elementsSlice = pageElements.slice(sourceIndex); // elements after
			const payload = { ...commonPayload, elementsSlice, direction: 'out' };
			const firstDirectionOutIndex = findDirectionIndex(payload);

			currentElementsInside = pageElements.slice(
				sourceIndex + 1,
				sourceIndex + firstDirectionOutIndex
			);
			break;
		}

		case 'out': {
			const elementsSlice = pageElements.slice(0, sourceIndex).reverse(); // elements before
			const payload = { ...commonPayload, elementsSlice, direction: 'in' };
			const firstDirectionInIndex = findDirectionIndex(payload);

			currentElementsInside = pageElements.slice(sourceIndex - firstDirectionInIndex, sourceIndex);
			break;
		}

		default:
			break;
	}

	return currentElementsInside;
};

export const getQuestionDeckTypes = (
	elementsInside: string[],
	elements: Entity<PageElement>
): ElementType[] | undefined =>
	elementsInside.length === 0
		? undefined
		: elementsInside.map((elementId) => elements.entities[elementId].type);

export const validateAllowedElementsInside = (payload: DoubleElementValidationPayload) => {
	const { draggableElement, sourceIndex, destinationIndex, elements } = payload;

	if (
		(draggableElement.direction === 'in' && destinationIndex > sourceIndex) ||
		(draggableElement.direction === 'out' && destinationIndex < sourceIndex) ||
		destinationIndex === sourceIndex
	)
		return; // means no new elements added to the double element

	const newElementsInside = getNewElementsInside(payload);
	const currentElementsInside = getCurrentElementsInside(payload);

	if (draggableElement.type === ElementType.QuestionDeck) {
		newElementsInside.forEach((elementId) => validateAllowedInQuestionDeck(elementId, elements));

		const questionDeckTypes = getQuestionDeckTypes(newElementsInside, elements);

		validateMutuallyExclusiveTypesInQuestionDeck(newElementsInside, questionDeckTypes, elements);

		if (questionDeckTypes.includes(ElementType.PollResults))
			validatePollAliasesInQuestionDeck([...currentElementsInside, ...newElementsInside], elements);

		if (
			questionDeckTypes.includes(ElementType.MCQuestion) &&
			[...currentElementsInside, ...newElementsInside].length > MAX_MCQ_IN_QUESTION_DECK
		)
			throw new Error(`The maximum allowed number of MCQ is ${MAX_MCQ_IN_QUESTION_DECK}`);
	}

	if (draggableElement.type === ElementType.NestingInstruction) {
		newElementsInside.forEach((elementId) =>
			validateAllowedInNestingInstruction(elementId, elements)
		);
	}
};

export const findNearestDoubleElement = (
	pageElements: string[],
	index: number,
	elements: Entity<PageElement>,
	direction: 'in' | 'out' = 'out'
) => {
	const elementsSlice =
		direction === 'out' ? pageElements.slice(index) : pageElements.slice(0, index);

	const nearestDoubleElementId = elementsSlice.find((elementId) => {
		const item = elements.entities[elementId];
		return isDoubleElement(item.type) && (item as DoubleElements).direction === direction;
	});

	return elements.entities[nearestDoubleElementId];
};

export const getElementsInsideByNearestDouble = ({
	nearestDoubleElement,
	destinationIndex,
	pageElements,
	elements
}) => {
	const elementsSlice = pageElements.slice(0, destinationIndex + 1).reverse(); // elements before
	const tempPayload = {
		elements,
		elementType: nearestDoubleElement.type,
		elementsSlice,
		direction: 'in'
	};
	const nearestInElementIndex = destinationIndex - findDirectionIndex(tempPayload);
	const nearestOutElementIndex = pageElements.indexOf(nearestDoubleElement.family_id);

	const currentElementsInside = pageElements.slice(
		nearestInElementIndex + 1,
		nearestOutElementIndex
	);
	return currentElementsInside;
};

export const getDestinationIndented = (payload: InsideDoubleValidationPayload) => {
	const { pageElements, sourceIndex, destinationIndex, elements } = payload;

	const destinationItemId = pageElements[destinationIndex];
	const destinationItem = elements.entities[destinationItemId];

	const direction = destinationIndex < sourceIndex ? 'out' : 'in';
	return (
		destinationItem.indent ||
		(isDoubleElement(destinationItem.type) &&
			(destinationItem as DoubleElements).direction === direction)
	);
};

export const validateIfInsideDouble = (payload: InsideDoubleValidationPayload) => {
	const { pageElements, sourceIndex, destinationIndex, elements } = payload;

	const sourceItemId = pageElements[sourceIndex];
	const sourceItem = elements.entities[sourceItemId];

	const indent = getDestinationIndented(payload);
	if (!indent) return;

	const nearestDoubleElement = findNearestDoubleElement(pageElements, destinationIndex, elements);
	if (nearestDoubleElement.type === ElementType.QuestionDeck) {
		validateAllowedInQuestionDeck(pageElements[sourceIndex], elements);

		const payload = { nearestDoubleElement, destinationIndex, pageElements, elements };
		const currentElementsInside = getElementsInsideByNearestDouble(payload);
		const newElementsInside = [sourceItem.family_id];

		const combinedNewElementsInside = [...currentElementsInside, ...newElementsInside];

		const questionDeckTypes = getQuestionDeckTypes(combinedNewElementsInside, elements);

		validateMutuallyExclusiveTypesInQuestionDeck(newElementsInside, questionDeckTypes, elements);
		if (questionDeckTypes.includes(ElementType.PollResults))
			validatePollAliasesInQuestionDeck(combinedNewElementsInside, elements);
	}
	if (nearestDoubleElement.type === ElementType.NestingInstruction) {
		validateAllowedInNestingInstruction(pageElements[sourceIndex], elements);
	}
};
